import { findApplication, normalizeOrigin, type ApplicationRegistry } from '@traceio-org/platform-contracts';
import type { AdminConfig } from './config';

export type AdminRegistration = { ok: true; audience: string } | { ok: false; errors: string[] };

function sameOrigin(a: string, b: string): boolean {
  try {
    return normalizeOrigin(a) === normalizeOrigin(b);
  } catch {
    return false;
  }
}

/**
 * The second startup guard (after the build settings): Trace Admin runs only where platform-api's registry,
 * for this very environment, registers it, with the configured hub, and never as a navigation fixture.
 * The audience it returns is the registry's admin audience: the one audience platform-api's staff routes
 * accept (ADMIN_API_AUDIENCE), so the session asks for exactly the tokens those routes take. Anything else
 * refuses to start, before the gate runs and before any token is requested.
 */
export function checkAdminRegistration(
  registry: ApplicationRegistry,
  config: AdminConfig,
  pageOrigin: string,
): AdminRegistration {
  const errors: string[] = [];
  if (registry.environment !== config.environment) {
    errors.push(
      `The registry is for ${registry.environment} but Trace Admin is built for ${config.environment}.`,
    );
  }
  if (!sameOrigin(registry.hub.origin, config.hubOrigin)) {
    errors.push(
      `VITE_HUB_ORIGIN is ${config.hubOrigin} but the registry's account hub is ${registry.hub.origin}.`,
    );
  }
  const application = findApplication(registry, 'admin');
  if (!application) {
    errors.push('The registry has no Trace Admin application.');
    return { ok: false, errors };
  }
  const destination = application.destination;
  if (application.status === 'unavailable' || !destination) {
    errors.push('Trace Admin is not deployed in this environment.');
  } else {
    if (destination.fixture) {
      errors.push('The registry marks Trace Admin as a navigation fixture; Trace Admin is never a fixture.');
    }
    if (!sameOrigin(destination.origin, pageOrigin)) {
      errors.push(`This page runs at ${pageOrigin}, but Trace Admin is registered at ${destination.origin}.`);
    }
  }
  // A build carrying another application's (or another environment's) Auth0 client would sign staff in
  // somewhere the registry does not expect; when both name the client, they must agree.
  if (config.auth0 && application.auth0ClientId && application.auth0ClientId !== config.auth0.clientId) {
    errors.push(
      `VITE_AUTH0_CLIENT_ID is ${config.auth0.clientId} but the registry gives Trace Admin ${application.auth0ClientId}.`,
    );
  }
  if (!application.audience) errors.push('The registry gives Trace Admin no audience.');
  if (errors.length > 0 || !application.audience) return { ok: false, errors };
  return { ok: true, audience: application.audience };
}
